import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import FinancePageShell from "./common/FinancePageShell";
import FinanceSectionCard from "./common/FinanceSectionCard";
import FinanceStatCard from "./common/FinanceStatCard";
import FinanceDataTable, {
  FinanceDataTableColumn,
} from "./common/FinanceDataTable";
import { financeApi } from "./financeApi";
import { formatDateTime, formatMoney } from "./common/formatters";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useAdminAccess } from "../Analytics/shared/AdminAccess/AdminAccessContext";

type PaymentRow = {
  _id?: string;
  entityType?: string;
  entityId?: string;
  orderId?: string;
  deliveryId?: string;
  payerUserId?: string;
  ownerUserId?: string;
  ownerId?: string;
  sellerUserId?: string;
  accountUserId?: string;
  beneficiaryUserId?: string;
  recipientUserId?: string;
  createdBy?: string;
  method?: string;
  provider?: string;
  status?: string;
  amount?: number;
  accountAmount?: number;
  platformPaymentAmount?: number;
  currency?: string;
  reference?: string;
  chargeId?: string;
  providerPaymentId?: string;
  paidAt?: string;
  createdAt?: string;
  accountUserIds?: string[];
  metadata?: Record<string, any>;
};

type SalesAllocationRow = {
  _id?: string;
  orderId?: string;
  entityId?: string;
  sellerUserId?: string;
  ownerUserId?: string;
  grossAmount?: number;
  amount?: number;
  currency?: string;
};

type PaymentSummary = {
  count: number;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
};

const clean = (value: unknown) => String(value || "").trim();

const normalizeRole = (value: unknown) =>
  clean(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const toArray = <T,>(payload: any): T[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;

  return [];
};

const getStatusScheme = (status?: string) => {
  const normalized = clean(status).toLowerCase();

  if (
    normalized === "paid" ||
    normalized === "success" ||
    normalized === "completed" ||
    normalized === "cash_collected" ||
    normalized === "cash_reconciled"
  ) {
    return "green";
  }

  if (
    normalized === "failed" ||
    normalized === "cancelled" ||
    normalized === "refunded"
  ) {
    return "red";
  }

  if (
    normalized === "pending" ||
    normalized === "processing" ||
    normalized === "cash_expected"
  ) {
    return "orange";
  }

  return "gray";
};

const getPaymentAccountIds = (row: PaymentRow) => {
  const ids = new Set<string>();

  const add = (value: unknown) => {
    const id = clean(value);
    if (id) ids.add(id);
  };

  const addMany = (values: unknown) => {
    if (!Array.isArray(values)) return;
    values.forEach(add);
  };

  add(row.ownerUserId);
  add(row.ownerId);
  add(row.sellerUserId);
  add(row.accountUserId);
  add(row.beneficiaryUserId);
  add(row.recipientUserId);
  add(row.createdBy);
  addMany(row.accountUserIds);

  const metadata = row.metadata || {};

  add(metadata.ownerUserId);
  add(metadata.ownerId);
  add(metadata.sellerUserId);
  add(metadata.accountUserId);
  add(metadata.beneficiaryUserId);
  add(metadata.recipientUserId);
  add(metadata.createdBy);
  addMany(metadata.accountUserIds);
  addMany(metadata.sellerUserIds);
  addMany(metadata.ownerUserIds);

  /*
   * Delivery payments belong to the sending account. For order and booking
   * payments payerUserId is normally the customer, so it must not be used as
   * the seller/owner unless no stronger ownership field is available.
   */
  if (ids.size === 0 && clean(row.entityType).toLowerCase() === "delivery") {
    add(row.payerUserId);
  }

  return ids;
};

const getAllocationOrderId = (row: SalesAllocationRow) =>
  clean(row.orderId || row.entityId);

const buildOwnOrderAmounts = (
  salesRows: SalesAllocationRow[],
  userId: string,
) => {
  const amounts = new Map<string, number>();

  salesRows.forEach((row) => {
    const sellerUserId = clean(row.sellerUserId || row.ownerUserId);
    const orderId = getAllocationOrderId(row);

    if (!orderId || sellerUserId !== userId) return;

    const amount = Number(row.grossAmount ?? row.amount ?? 0);
    amounts.set(orderId, Number(amounts.get(orderId) || 0) + amount);
  });

  return amounts;
};

const isPaymentOwnedByAccount = ({
  row,
  userId,
  ownOrderAmounts,
}: {
  row: PaymentRow;
  userId: string;
  ownOrderAmounts: Map<string, number>;
}) => {
  const normalizedUserId = clean(userId);
  if (!normalizedUserId) return false;

  if (getPaymentAccountIds(row).has(normalizedUserId)) {
    return true;
  }

  if (clean(row.entityType).toLowerCase() === "order") {
    const orderId = clean(row.orderId || row.entityId);
    return Boolean(orderId && ownOrderAmounts.has(orderId));
  }

  return false;
};

const scopePaymentRow = ({
  row,
  userId,
  ownOrderAmounts,
}: {
  row: PaymentRow;
  userId: string;
  ownOrderAmounts: Map<string, number>;
}): PaymentRow => {
  if (clean(row.entityType).toLowerCase() !== "order") {
    return row;
  }

  const orderId = clean(row.orderId || row.entityId);
  const ownAmount = Number(ownOrderAmounts.get(orderId) || 0);

  if (ownAmount <= 0) return row;

  return {
    ...row,
    platformPaymentAmount: Number(row.amount || 0),
    accountAmount: ownAmount,
    amount: ownAmount,
    accountUserIds: Array.from(
      new Set([...(row.accountUserIds || []), userId].filter(Boolean)),
    ),
  };
};

const buildSummary = (rows: PaymentRow[]): PaymentSummary => {
  const paidStatuses = new Set([
    "paid",
    "success",
    "completed",
    "cash_collected",
    "cash_reconciled",
  ]);

  const pendingStatuses = new Set(["pending", "processing", "cash_expected"]);

  return rows.reduce<PaymentSummary>(
    (summary, row) => {
      const amount = Number(row.accountAmount ?? row.amount ?? 0);
      const status = clean(row.status).toLowerCase();

      summary.count += 1;
      summary.totalAmount += amount;

      if (paidStatuses.has(status)) {
        summary.paidAmount += amount;
      }

      if (pendingStatuses.has(status)) {
        summary.pendingAmount += amount;
      }

      return summary;
    },
    {
      count: 0,
      totalAmount: 0,
      paidAmount: 0,
      pendingAmount: 0,
    },
  );
};

const AdminPaymentsPage: React.FC = () => {
  const toast = useToast();
  const { theme } = useCustomTheme();
  const {
    userId,
    role,
    isSuperAdmin,
    loading: accessLoading,
  } = useAdminAccess();

  const normalizedRole = normalizeRole(role);
  const hasFinanceAdminAccess = isSuperAdmin || normalizedRole === "admin";

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [summary, setSummary] = useState<PaymentSummary>({
    count: 0,
    totalAmount: 0,
    paidAmount: 0,
    pendingAmount: 0,
  });
  const [query, setQuery] = useState("");

  const load = useCallback(
    async (q = "") => {
      if (accessLoading) return;

      if (!userId || !hasFinanceAdminAccess) {
        setRows([]);
        setSummary(buildSummary([]));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const paymentParams: Record<string, string | number> = {
          q: clean(q),
          limit: 500,
        };

        if (!isSuperAdmin) {
          paymentParams.accountUserId = userId;
          paymentParams.ownerUserId = userId;
        }

        const [paymentsResult, salesResult] = await Promise.allSettled([
          financeApi.getAdminPayments(paymentParams),
          isSuperAdmin
            ? Promise.resolve({ data: [] })
            : financeApi.getAdminSales({
                sellerUserId: userId,
                accountUserId: userId,
                limit: 500,
              }),
        ]);

        if (paymentsResult.status !== "fulfilled") {
          throw paymentsResult.reason;
        }

        const paymentRows = toArray<PaymentRow>(paymentsResult.value);
        const salesRows =
          salesResult.status === "fulfilled"
            ? toArray<SalesAllocationRow>(salesResult.value)
            : [];

        if (salesResult.status === "rejected") {
          console.warn(
            "Failed to load sales allocations for payment ownership:",
            salesResult.reason,
          );
        }

        const ownOrderAmounts = buildOwnOrderAmounts(salesRows, userId);

        const visibleRows = isSuperAdmin
          ? paymentRows
          : paymentRows
              .filter((row) =>
                isPaymentOwnedByAccount({
                  row,
                  userId,
                  ownOrderAmounts,
                }),
              )
              .map((row) =>
                scopePaymentRow({
                  row,
                  userId,
                  ownOrderAmounts,
                }),
              );

        setRows(visibleRows);
        setSummary(buildSummary(visibleRows));
      } catch (error: any) {
        console.error("Failed to load payment records:", error);

        toast({
          status: "error",
          title: "Failed to load payment records",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Please try again.",
          duration: 4000,
          isClosable: true,
        });

        setRows([]);
        setSummary(buildSummary([]));
      } finally {
        setLoading(false);
      }
    },
    [accessLoading, hasFinanceAdminAccess, isSuperAdmin, toast, userId],
  );

  useEffect(() => {
    if (accessLoading) return;
    void load("");
  }, [accessLoading, load]);

  const columns = useMemo<Array<FinanceDataTableColumn<PaymentRow>>>(
    () => [
      {
        key: "entity",
        header: "Entity",
        render: (row) => (
          <>
            <Text fontWeight="700" color={theme.text}>
              {row.entityType || "—"}
            </Text>
            <Text fontSize="xs" color={theme.textMuted}>
              {row.entityId || row.orderId || row.deliveryId || "—"}
            </Text>
          </>
        ),
      },
      {
        key: "account",
        header: "Account",
        render: (row) =>
          isSuperAdmin
            ? Array.from(getPaymentAccountIds(row)).join(", ") || "Platform"
            : "Current account",
      },
      {
        key: "payer",
        header: "Payer",
        render: (row) => row.payerUserId || "—",
      },
      {
        key: "method",
        header: "Method",
        render: (row) => row.method || "—",
      },
      {
        key: "provider",
        header: "Provider",
        render: (row) => row.provider || "—",
      },
      {
        key: "status",
        header: "Status",
        render: (row) => (
          <Badge colorScheme={getStatusScheme(row.status)}>
            {row.status || "pending"}
          </Badge>
        ),
      },
      {
        key: "amount",
        header: isSuperAdmin ? "Amount" : "Your Gross Amount",
        isNumeric: true,
        render: (row) => (
          <>
            <Text fontWeight="700">
              {formatMoney(row.accountAmount ?? row.amount, row.currency)}
            </Text>

            {!isSuperAdmin &&
              Number(row.platformPaymentAmount || 0) >
                Number(row.accountAmount || row.amount || 0) && (
                <Text fontSize="xs" color={theme.textMuted}>
                  Multi-seller checkout
                </Text>
              )}
          </>
        ),
      },
      {
        key: "reference",
        header: "Reference",
        render: (row) =>
          row.reference || row.chargeId || row.providerPaymentId || "—",
      },
      {
        key: "paidAt",
        header: "Paid At",
        render: (row) => formatDateTime(row.paidAt),
      },
      {
        key: "createdAt",
        header: "Created",
        render: (row) => formatDateTime(row.createdAt),
      },
    ],
    [isSuperAdmin, theme.text, theme.textMuted],
  );

  if (accessLoading || loading) {
    return (
      <FinancePageShell title="Payments">
        <VStack py={10}>
          <Spinner />
          <Text color={theme.textMuted}>Loading payment records...</Text>
        </VStack>
      </FinancePageShell>
    );
  }

  if (!userId) {
    return (
      <FinancePageShell title="Payments">
        <Center minH="260px">
          <Text color={theme.textMuted}>
            Sign in before opening payment records.
          </Text>
        </Center>
      </FinancePageShell>
    );
  }

  if (!hasFinanceAdminAccess) {
    return (
      <FinancePageShell title="Payments">
        <Center minH="260px">
          <Text color={theme.textMuted}>
            Only admin and superadmin accounts can open payment records.
          </Text>
        </Center>
      </FinancePageShell>
    );
  }

  return (
    <FinancePageShell
      title="Payment Records"
      subtitle={
        isSuperAdmin
          ? "Every customer payment row across all platform accounts."
          : "Customer payment rows linked only to your orders and services."
      }
    >
      <HStack mb={4} spacing={2} flexWrap="wrap">
        <Badge
          borderRadius="full"
          px={3}
          py={1}
          colorScheme={isSuperAdmin ? "purple" : "blue"}
        >
          {isSuperAdmin ? "All platform accounts" : "Current account only"}
        </Badge>

        <Text fontSize="xs" color={theme.textMuted}>
          Role: {role || "user"} · UID: {userId}
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
        <FinanceStatCard label="Records" value={String(summary.count)} />
        <FinanceStatCard
          label={isSuperAdmin ? "Total Amount" : "Your Gross Amount"}
          value={formatMoney(summary.totalAmount)}
        />
        <FinanceStatCard
          label="Paid Amount"
          value={formatMoney(summary.paidAmount)}
        />
        <FinanceStatCard
          label="Pending Amount"
          value={formatMoney(summary.pendingAmount)}
        />
      </SimpleGrid>

      <FinanceSectionCard
        title={isSuperAdmin ? "All Payments" : "Your Payments"}
        rightSlot={
          <HStack w={{ base: "100%", md: "auto" }} flexWrap="wrap">
            <Input
              placeholder="Search payment, order, ref, payer..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  void load(query);
                }
              }}
              bg={theme.inputBg}
              borderColor={theme.inputBorder}
              _placeholder={{ color: theme.inputPlaceholder }}
              minW={{ base: "100%", md: "280px" }}
            />

            <Button
              size="sm"
              onClick={() => void load(query)}
              color={theme.textOnPrimary}
              bgImage={theme.gradients.primaryButton}
              _hover={{
                bgImage: theme.gradients.primaryButtonHover,
              }}
              isLoading={loading}
            >
              Search
            </Button>
          </HStack>
        }
      >
        <FinanceDataTable
          rows={rows}
          columns={columns}
          getRowKey={(row, index) =>
            String(
              row._id ||
                `${row.entityType || "payment"}:${row.entityId || index}`,
            )
          }
          emptyText={
            isSuperAdmin
              ? "No payment records found."
              : "No payment records are linked to your account."
          }
          initialPageSize={10}
        />
      </FinanceSectionCard>
    </FinancePageShell>
  );
};

export default AdminPaymentsPage;
